import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Play, Pause, MapPin } from "lucide-react";
import api from "../lib/api";
import { MaskedLines, FadeUp, Eyebrow, Marquee, EASE } from "../components/Reveal";
import HeroCollage from "../components/HeroCollage";
import Testimonials from "../components/Testimonials";
import { Waveform } from "../components/AudioArt";

const CHAPTERS = [
  {
    num: "01",
    title: "Sonic Precision",
    body: "Every transient placed with intent. Precision chains built in Pro Tools and Studio One, tuned rooms, and mixes that translate from studio monitors to phone speakers without losing their soul.",
  },
  {
    num: "02",
    title: "Feminine Authority in Sound",
    body: "A woman's ear behind the board. Empress brings taste, patience and standards to a craft that has gatekept both — your record is handled with the same care she gives her own name.",
  },
  {
    num: "03",
    title: "The Empress Standard",
    body: "Nothing leaves the studio half-finished. Revisions until the record breathes, masters that hold up next to anything on your playlist, and files delivered organized — never scattered across text threads.",
  },
];

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [0.55, 0.1]);

  return (
    <section ref={ref} className="relative min-h-[92vh] bg-obsidian text-white overflow-hidden grain flex items-center" data-testid="hero-section">
      <motion.div style={{ y, opacity, backgroundImage: "url(/art-spectrogram.webp)" }} className="absolute inset-0 spectrogram-bg" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#0F0E13]/40 via-transparent to-[#0F0E13]" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0F0E13] via-[#0F0E13]/70 to-transparent lg:to-[#0F0E13]/20" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-24 lg:py-20 w-full grid gap-14 lg:grid-cols-12 items-center">
        <div className="lg:col-span-6">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.1 }}
            className="text-xs font-mono uppercase tracking-[0.3em] text-gold mb-8"
            data-testid="hero-eyebrow"
          >
            Recording · Mixing · Mastering — Nashville / ATL / Remote
          </motion.p>
          <h1 className="font-serif tracking-tight leading-[1.02] text-5xl sm:text-6xl lg:text-7xl xl:text-8xl" data-testid="hero-headline">
            <MaskedLines
              lines={["Your sound,", "finished by", "an Empress."]}
              lineClassName={""}
            />
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE, delay: 0.75 }}
            className="mt-8 max-w-md text-white/60 text-base sm:text-lg leading-relaxed"
            data-testid="hero-subtext"
          >
            A luxury studio experience for artists who hear the difference. Book a session, send your stems, and watch your record take its final form.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE, delay: 0.9 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <Link
              to="/book"
              data-testid="hero-book-session-btn"
              className="group px-8 py-4 rounded-full bg-gold text-obsidian font-semibold text-sm tracking-wide flex items-center gap-2 hover:brightness-110 transition-all"
            >
              Book a Session
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/services"
              data-testid="hero-services-btn"
              className="px-8 py-4 rounded-full border border-white/25 text-sm text-white/85 hover:border-gold hover:text-gold transition-colors"
            >
              Explore Services
            </Link>
          </motion.div>
        </div>
        <div className="lg:col-span-6">
          <HeroCollage scrollRef={ref} />
        </div>
      </div>

      <Waveform className="absolute -bottom-8 left-0 w-full h-28 opacity-30 pointer-events-none" strands={3} delay={1.4} />
    </section>
  );
}

function Manifesto() {
  return (
    <section className="py-24 lg:py-36 bg-background" data-testid="manifesto-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
        <FadeUp>
          <Eyebrow>The Manifesto</Eyebrow>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl mt-4 max-w-2xl leading-tight">
            Three chapters. <span className="italic text-purple-700">One standard.</span>
          </h2>
        </FadeUp>
        <div className="mt-16 grid gap-12 lg:grid-cols-3 lg:gap-10">
          {CHAPTERS.map((c, i) => (
            <FadeUp key={c.num} delay={i * 0.12} className="border-t border-purple-100 pt-8" data-testid={`manifesto-chapter-${c.num}`}>
              <span className="font-mono text-sm text-gold tracking-[0.3em]">{c.num}</span>
              <h3 className="font-serif text-2xl mt-4">{c.title}</h3>
              <p className="text-slate-500 text-sm leading-relaxed mt-4">{c.body}</p>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
}

function ReleasesSection() {
  const [releases, setReleases] = useState([]);
  const [playing, setPlaying] = useState(null);

  useEffect(() => {
    api.get("/releases").then((r) => setReleases(r.data)).catch(() => {});
  }, []);

  return (
    <section className="py-24 lg:py-32 bg-lilac-mist" data-testid="releases-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
        <FadeUp className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <Eyebrow>Selected Work</Eyebrow>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl mt-4">Featured <span className="italic text-purple-700">releases</span></h2>
          </div>
          <Link to="/releases" className="text-sm text-purple-700 hover:text-gold transition-colors flex items-center gap-2" data-testid="releases-view-all">
            All releases <ArrowRight size={15} />
          </Link>
        </FadeUp>
        <div className="mt-14 grid gap-8 md:grid-cols-3">
          {releases.slice(0, 3).map((r, i) => (
            <FadeUp key={r.release_id} delay={i * 0.12} className="trace-card bg-white rounded-3xl p-5 border border-purple-100" data-testid={`release-card-${i}`}>
              <div className="relative rounded-2xl overflow-hidden aspect-square group">
                <img src={r.artwork_url?.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${r.artwork_url}` : r.artwork_url} alt={`${r.title} artwork`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                <button
                  onClick={() => setPlaying(playing === r.release_id ? null : r.release_id)}
                  data-testid={`release-play-${i}`}
                  aria-label={`Play preview of ${r.title}`}
                  className="absolute inset-0 flex items-center justify-center bg-obsidian/0 group-hover:bg-obsidian/40 transition-colors"
                >
                  <span className="w-14 h-14 rounded-full bg-gold text-obsidian flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-xl">
                    {playing === r.release_id ? <Pause size={20} /> : <Play size={20} className="ml-0.5" />}
                  </span>
                </button>
                {playing === r.release_id && (
                  <div className="absolute bottom-3 left-3 flex items-end gap-[3px] h-4">
                    {[0, 1, 2, 3].map((b) => (
                      <span key={b} className="eq-bar w-[3px] h-4 bg-gold rounded-full" style={{ animationDelay: `${b * 0.13}s` }} />
                    ))}
                  </div>
                )}
              </div>
              <div className="pt-5 flex items-start justify-between gap-3">
                <div>
                  <h3 className="font-serif text-xl">{r.title}</h3>
                  <p className="text-sm text-slate-500 mt-1">{r.artist} · {r.year}</p>
                </div>
                <a href={r.stream_url} target="_blank" rel="noreferrer" data-testid={`release-stream-${i}`}
                   className="text-[11px] font-mono uppercase tracking-widest text-purple-700 border border-purple-200 rounded-full px-3 py-1.5 hover:border-gold hover:text-gold transition-colors shrink-0">
                  {r.platform}
                </a>
              </div>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
}

function WorkWithMe() {
  return (
    <section className="py-24 lg:py-32 bg-background" data-testid="work-with-me-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
        <FadeUp>
          <Eyebrow>Work With Me</Eyebrow>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl mt-4 max-w-xl leading-tight">
            Two doors into <span className="italic text-purple-700">the studio</span>
          </h2>
        </FadeUp>
        <div className="mt-14 grid gap-8 md:grid-cols-2">
          <FadeUp className="trace-card rounded-3xl bg-obsidian text-white p-10 grain relative overflow-hidden" data-testid="pathway-booking">
            <p className="font-mono text-xs tracking-[0.3em] text-gold">IN THE ROOM</p>
            <h3 className="font-serif text-3xl mt-4">Book a studio session</h3>
            <p className="text-white/60 text-sm leading-relaxed mt-4 max-w-sm">
              Recording sessions and hourly blocks in Nashville or Atlanta. Request your time — Empress confirms every session personally.
            </p>
            <Link to="/book" data-testid="pathway-booking-btn" className="inline-flex items-center gap-2 mt-8 text-sm text-gold group">
              Request a time <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </FadeUp>
          <FadeUp delay={0.12} className="trace-card rounded-3xl bg-white border border-purple-100 p-10" data-testid="pathway-mixmaster">
            <p className="font-mono text-xs tracking-[0.3em] text-purple-700">FROM ANYWHERE</p>
            <h3 className="font-serif text-3xl mt-4 text-obsidian">Get your record mixed</h3>
            <p className="text-slate-500 text-sm leading-relaxed mt-4 max-w-sm">
              Send stems remotely and receive finished mixes, instrumentals and acapellas — organized by version in your private portal.
            </p>
            <Link to="/services" data-testid="pathway-services-btn" className="inline-flex items-center gap-2 mt-8 text-sm text-purple-700 hover:text-gold transition-colors group">
              See services &amp; rates <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </FadeUp>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <div>
      <Hero />
      <Marquee items={["Recording", "Pro Tools & Studio One", "Precision Mixing", "Lock-In Packages", "Atmospheric Soundscapes"]} />
      <Manifesto />
      <ReleasesSection />
      <Testimonials />
      <WorkWithMe />
      <section className="bg-obsidian text-white grain relative py-24" data-testid="home-cta">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 text-center">
          <FadeUp>
            <p className="font-serif italic text-3xl sm:text-4xl lg:text-5xl leading-tight max-w-3xl mx-auto">
              "No time is confirmed until <span className="gold-gradient-text">Empress</span> says so."
            </p>
            <p className="text-white/50 text-sm mt-6 flex items-center justify-center gap-2">
              <MapPin size={14} className="text-gold" /> Nashville · Atlanta · Remote worldwide
            </p>
            <Link to="/book" data-testid="cta-book-btn" className="inline-flex items-center gap-2 mt-10 px-9 py-4 rounded-full bg-gold text-obsidian font-semibold text-sm hover:brightness-110 transition-all">
              Request your session <ArrowRight size={16} />
            </Link>
          </FadeUp>
        </div>
      </section>
    </div>
  );
}
